// src/utils/index.ts
function haversineDistance(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}
function toRadians(degrees) {
  return degrees * (Math.PI / 180);
}
function getBoundingBox(lat, lon, distanceKm) {
  const latDelta = distanceKm / 111.32;
  const lonDelta = distanceKm / (111.32 * Math.cos(toRadians(lat)));
  return {
    minLat: lat - latDelta,
    maxLat: lat + latDelta,
    minLon: lon - lonDelta,
    maxLon: lon + lonDelta
  };
}
function formatBRL(centavos) {
  const reais = centavos / 100;
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL"
  }).format(reais);
}
function parseBRL(value) {
  const cleaned = value.replace(/[R$\s.]/g, "").replace(",", ".");
  return Math.round(parseFloat(cleaned) * 100);
}
function formatDateBR(timestamp) {
  const date = typeof timestamp === "string" ? new Date(timestamp) : new Date(timestamp);
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric"
  }).format(date);
}
function formatTimeBR(timestamp) {
  const date = typeof timestamp === "string" ? new Date(timestamp) : new Date(timestamp);
  return new Intl.DateTimeFormat("pt-BR", {
    hour: "2-digit",
    minute: "2-digit"
  }).format(date);
}
function formatDateTimeBR(timestamp) {
  const date = typeof timestamp === "string" ? new Date(timestamp) : new Date(timestamp);
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  }).format(date);
}
function formatRelativeTime(timestamp) {
  const date = typeof timestamp === "string" ? new Date(timestamp) : new Date(timestamp);
  const now = /* @__PURE__ */ new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1e3 * 60));
  const diffHours = Math.floor(diffMs / (1e3 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1e3 * 60 * 60 * 24));
  if (diffMins < 1) return "agora";
  if (diffMins < 60) return `h\xE1 ${diffMins} ${diffMins === 1 ? "minuto" : "minutos"}`;
  if (diffHours < 24) return `h\xE1 ${diffHours} ${diffHours === 1 ? "hora" : "horas"}`;
  if (diffDays < 7) return `h\xE1 ${diffDays} ${diffDays === 1 ? "dia" : "dias"}`;
  return formatDateBR(date.getTime());
}
function generateId() {
  return crypto.randomUUID();
}
async function hashCPF(cpf) {
  const cleaned = cpf.replace(/\D/g, "");
  const encoder = new TextEncoder();
  const data = encoder.encode(cleaned);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}
function isValidCPF(cpf) {
  const cleaned = cpf.replace(/\D/g, "");
  if (cleaned.length !== 11) return false;
  if (/^(\d)\1+$/.test(cleaned)) return false;
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(cleaned[i]) * (10 - i);
  }
  let digit = 11 - sum % 11;
  if (digit >= 10) digit = 0;
  if (digit !== parseInt(cleaned[9])) return false;
  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(cleaned[i]) * (11 - i);
  }
  digit = 11 - sum % 11;
  if (digit >= 10) digit = 0;
  if (digit !== parseInt(cleaned[10])) return false;
  return true;
}
function formatCPF(cpf) {
  const cleaned = cpf.replace(/\D/g, "");
  return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
}
function truncate(text, maxLength) {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 3) + "...";
}
function calculatePlatformFee(amount) {
  return Math.round(amount * 0.1);
}
function calculateTotal(subtotal) {
  const platformFee = calculatePlatformFee(subtotal);
  return {
    subtotal,
    platformFee,
    total: subtotal + platformFee
  };
}
var TOKEN_REWARDS = {
  HOST_MEAL_COMPLETED: 100,
  GUEST_MEAL_COMPLETED: 50,
  FIRST_MEAL_BONUS: 500,
  REFERRAL_BONUS: 200,
  GOOD_REVIEW_BONUS: 25
};
var BRAZILIAN_STATES = {
  AC: "Acre",
  AL: "Alagoas",
  AP: "Amap\xE1",
  AM: "Amazonas",
  BA: "Bahia",
  CE: "Cear\xE1",
  DF: "Distrito Federal",
  ES: "Esp\xEDrito Santo",
  GO: "Goi\xE1s",
  MA: "Maranh\xE3o",
  MT: "Mato Grosso",
  MS: "Mato Grosso do Sul",
  MG: "Minas Gerais",
  PA: "Par\xE1",
  PB: "Para\xEDba",
  PR: "Paran\xE1",
  PE: "Pernambuco",
  PI: "Piau\xED",
  RJ: "Rio de Janeiro",
  RN: "Rio Grande do Norte",
  RS: "Rio Grande do Sul",
  RO: "Rond\xF4nia",
  RR: "Roraima",
  SC: "Santa Catarina",
  SP: "S\xE3o Paulo",
  SE: "Sergipe",
  TO: "Tocantins"
};
var DIETARY_RESTRICTIONS = [
  "Vegetariano",
  "Vegano",
  "Sem Gl\xFAten",
  "Sem Lactose",
  "Kosher",
  "Halal",
  "Sem Frutos do Mar",
  "Sem Nozes",
  "Low Carb"
];
var CUISINE_TYPES = [
  "Brasileira",
  "Italiana",
  "Japonesa",
  "Mexicana",
  "\xC1rabe",
  "Chinesa",
  "Indiana",
  "Francesa",
  "Tailandesa",
  "Portuguesa",
  "Nordestina",
  "Mineira",
  "Ga\xFAcha",
  "Baiana",
  "Contempor\xE2nea"
];
export {
  BRAZILIAN_STATES,
  CUISINE_TYPES,
  DIETARY_RESTRICTIONS,
  TOKEN_REWARDS,
  calculatePlatformFee,
  calculateTotal,
  formatBRL,
  formatCPF,
  formatDateBR,
  formatDateTimeBR,
  formatRelativeTime,
  formatTimeBR,
  generateId,
  getBoundingBox,
  hashCPF,
  haversineDistance,
  isValidCPF,
  parseBRL,
  toRadians,
  truncate
};
//# sourceMappingURL=index.js.map